import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import PCMBadge from "../components/PCMBadge";
import AnswerComments from "../components/AnswerComments";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Play,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Target,
  Lightbulb,
  Anchor,
  FileText,
  Save,
  Compass,
  Battery,
  Brain,
  MessageCircle,
  Shield,
  Clock,
  Download,
} from "lucide-react";

import { API } from "../utils/api";

// FLOW Module Content - Textes éditoriaux
const FLOW_MODULE_CONTENT = {
  1: {
    icon: Compass,
    intention: "T'aider à arrêter de te battre contre ton propre fonctionnement et poser une base claire pour la suite du parcours.",
    intro: `Tu fais déjà beaucoup de choses bien.
Mais une partie de ta fatigue ne vient pas de ce que tu fais.
Elle vient de comment tu t'y prends, surtout sous pression.

Ce module te permet de mettre des mots clairs sur :
• ta manière naturelle de fonctionner
• ce qui t'aide vraiment
• ce qui te coûte inutilement

Pas pour te coller une étiquette.
Pour te redonner des marges de manœuvre.`,
    promise: [
      "ce qui te recharge réellement",
      "ce qui te vide",
      "comment retrouver de la clarté quand tout s'emmêle"
    ],
    transition: "Maintenant que ton fonctionnement est plus lisible, on peut s'occuper de ce qui fait vraiment la différence dans la durée : ton énergie."
  },
  2: {
    icon: Battery,
    intention: "Sortir du cycle pics d'énergie → épuisement → récupération forcée et installer un rythme plus stable.",
    intro: `Ton énergie n'est pas une question de volonté.
C'est une question de besoins nourris ou non.

Quand certains besoins passent au second plan trop longtemps,
le corps et le mental finissent par tirer le frein à main.

Ce module t'aide à :
• comprendre ce qui te recharge vraiment aujourd'hui
• repérer tes fuites d'énergie
• ajuster ton rythme sans tout changer`,
    promise: [
      "un plan simple sur 7 jours",
      "une action concrète pour remonter ton niveau d'énergie",
      "un premier ajustement réaliste (pas un grand reset irréaliste)"
    ],
    transition: "Quand l'énergie baisse, le stress monte. Le module suivant t'aide à reprendre la main avant que ça déborde."
  },
  3: {
    icon: Brain,
    intention: "Te permettre de repérer plus tôt les moments où tu bascules en pilotage automatique.",
    intro: `Le stress n'arrive jamais d'un coup.
Il envoie toujours des signaux avant.

Le problème, ce n'est pas le stress.
C'est de le repérer trop tard, quand les réactions ont déjà pris le dessus.

Ce module t'aide à :
• identifier tes signaux faibles
• comprendre tes automatismes
• créer un espace entre ce qui se passe et ta réaction`,
    promise: [
      "tu sais reconnaître les premiers signes de tension",
      "tu disposes d'un rituel simple pour réguler",
      "tu récupères une marge de choix"
    ],
    transition: "Quand le stress baisse, la parole devient plus claire. On peut alors travailler ce qui est souvent le plus délicat : dire les choses sans se trahir."
  },
  4: {
    icon: MessageCircle,
    intention: "Aider à dire ce qui doit être dit sans s'écraser, sans attaquer, sans ruminer après coup.",
    intro: `Beaucoup de tensions ne viennent pas de ce qui est dit.
Elles viennent de ce qui n'est pas dit, ou mal posé.

Ici, il ne s'agit pas de "mieux communiquer".
Il s'agit de tenir ta posture quand un message est important.`,
    promise: [
      "moins d'ambiguïté",
      "moins de non-dits",
      "plus de cohérence entre ce que tu penses, dis et fais"
    ],
    transition: "Dire les choses clairement, c'est une chose. Tenir dans la durée, sans te sur-adapter, en est une autre."
  },
  5: {
    icon: Shield,
    intention: "T'aider à poser un cadre tenable pour toi, sans te durcir ni te renier.",
    intro: `Dire oui tout le temps a un coût.
Souvent invisible au début.
Très clair à la fin.

Ce module ne parle pas de rigidité.
Il parle de respect de toi, de clarté, et de cohérence.`,
    promise: [
      "tu identifies une situation où tu te sur-adaptes",
      "tu poses une limite claire",
      "tu ajustes ta posture sans conflit inutile"
    ],
    transition: "Tu as maintenant des repères solides. Il reste une étape clé : consolider et tenir dans la durée."
  },
  6: {
    icon: Clock,
    intention: "Transformer les prises de conscience en système durable.",
    intro: `Tu ne repars pas avec une motivation fragile.
Tu repars avec des repères clairs.

Ce module sert à :
• consolider ce qui fonctionne
• anticiper les zones de vigilance
• construire un plan réaliste pour la suite`,
    promise: [
      "un plan clair sur 90 jours",
      "2–3 rituels adaptés à ton fonctionnement",
      "une vigilance précise pour éviter de retomber dans l'ancien mode"
    ],
    closing: `FLOW ne t'a pas appris à faire plus.
Il t'a aidé à faire plus juste.

Tu sais maintenant :
• comment te réguler
• comment te repositionner
• comment tenir dans la durée`
  }
};

const ModulePage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [answers, setAnswers] = useState({});
  const [answersData, setAnswersData] = useState([]); // Full answer objects with IDs
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("intro");
  const [savingAnswers, setSavingAnswers] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);
  const saveTimeoutRefs = useRef({});

  // Get FLOW content for this module (based on module number, not ID)
  const flowContent = module?.number ? FLOW_MODULE_CONTENT[module.number] : null;
  const ModuleIcon = flowContent?.icon || BookOpen;

  useEffect(() => {
    fetchModuleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, token]);

  const fetchModuleData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [moduleRes, progressRes, answersRes] = await Promise.all([
        fetch(`${API}/modules/${moduleId}`, { headers, credentials: "include" }),
        fetch(`${API}/users/me/progress`, { headers, credentials: "include" }),
        fetch(`${API}/modules/${moduleId}/answers`, { headers, credentials: "include" }),
      ]);

      if (moduleRes.ok) {
        const moduleData = await moduleRes.json();
        setModule(moduleData);
      } else {
        toast.error("Module non trouvé");
        navigate("/dashboard");
        return;
      }

      if (progressRes.ok) {
        const progressData = await progressRes.json();
        const moduleProgress = progressData.find((p) => p.module_id === moduleId);
        setProgress(moduleProgress);
      }

      if (answersRes.ok) {
        const answersDataArr = await answersRes.json();
        // Store full answer data for conversation links
        setAnswersData(answersDataArr);
        // Convert to object keyed by block_id
        const answersMap = {};
        answersDataArr.forEach((a) => {
          answersMap[a.block_id] = a.answer_text;
        });
        setAnswers(answersMap);
      }
    } catch (error) {
      console.error("Error fetching module:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Helper to get answer_id for a block
  const getAnswerId = (blockId) => {
    const answer = answersData.find(a => a.block_id === blockId);
    return answer?.answer_id || null;
  };

  const updateProgress = async (updateData) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/modules/${moduleId}/progress`, {
        method: "POST",
        headers,
        body: JSON.stringify(updateData),
        credentials: "include",
      });

      if (response.ok) {
        const updatedProgress = await response.json();
        setProgress(updatedProgress);
        return true;
      }
    } catch (error) {
      console.error("Error updating progress:", error);
    }
    return false;
  };

  const saveAnswer = useCallback(async (blockId, answerText) => {
    try {
      setSavingAnswers((prev) => ({ ...prev, [blockId]: true }));
      
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/modules/${moduleId}/answers`, {
        method: "POST",
        headers,
        body: JSON.stringify({ block_id: blockId, answer_text: answerText }),
        credentials: "include",
      });

      if (response.ok) {
        setSavingAnswers((prev) => ({ ...prev, [blockId]: "saved" }));
        setTimeout(() => {
          setSavingAnswers((prev) => ({ ...prev, [blockId]: false }));
        }, 2000);
      }
    } catch (error) {
      console.error("Error saving answer:", error);
      setSavingAnswers((prev) => ({ ...prev, [blockId]: false }));
    }
  }, [token, moduleId]);

  const handleAnswerChange = useCallback((blockId, value) => {
    setAnswers((prev) => ({ ...prev, [blockId]: value }));
    
    // Clear existing timeout
    if (saveTimeoutRefs.current[blockId]) {
      clearTimeout(saveTimeoutRefs.current[blockId]);
    }
    
    // Autosave after 1 second of no typing
    saveTimeoutRefs.current[blockId] = setTimeout(() => {
      saveAnswer(blockId, value);
    }, 1000);
  }, [saveAnswer]);

  const handleVideoWatched = async () => {
    const success = await updateProgress({ video_watched: true });
    if (success) {
      toast.success("Vidéo marquée comme vue");
      setActiveSection("content");
    }
  };

  const handleExerciseComplete = async (exerciseId) => {
    const success = await updateProgress({ exercise_completed: exerciseId });
    if (success) {
      toast.success("Exercice complété");
    }
  };

  const handleValidateModule = async () => {
    const success = await updateProgress({ validation_completed: true });
    if (success) {
      toast.success("Module validé ! Bravo 🎉");
      setTimeout(() => navigate("/dashboard"), 1500);
    }
  };

  const getBlocksByType = (type) => {
    return module?.content_blocks?.filter((b) => b.type === type) || [];
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  if (!module) {
    return null;
  }

  const textBlocks = getBlocksByType("text");
  const questionBlocks = getBlocksByType("question");
  const anchorBlocks = getBlocksByType("anchor");
  const exercises = module.exercises || [];

  const canValidate =
    progress?.video_watched &&
    exercises.filter((e) => !e.is_optional).every((e) =>
      progress?.exercises_completed?.includes(e.exercise_id)
    );

  const hasAnswers = Object.values(answers).some(a => a && a.trim());

  const handleExportModulePdf = async () => {
    setExportingPdf(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/export/journal/${moduleId}`, {
        headers,
        credentials: "include"
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FLOW_Module${module.number}_${user?.name?.replace(/ /g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Module exporté avec succès !");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'export");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 lg:ml-64" data-testid="module-page">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center text-slate-600 hover:text-[#101E4D] transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour au tableau de bord
            </button>
            <div className="flex items-center space-x-3">
              {hasAnswers && (
                <Button
                  onClick={handleExportModulePdf}
                  disabled={exportingPdf}
                  variant="outline"
                  size="sm"
                  className="border-[#101E4D] text-[#101E4D] hover:bg-[#101E4D]/5"
                  data-testid="export-module-btn"
                >
                  {exportingPdf ? (
                    <div className="w-4 h-4 border-2 border-[#101E4D] border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Exporter
                </Button>
              )}
              {user?.pcm_base && <PCMBadge type={user.pcm_base} size="sm" />}
              {user?.pcm_phase && <PCMBadge type={user.pcm_phase} size="sm" />}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto p-6 lg:p-8">
          {/* Module Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#101E4D] rounded-xl flex items-center justify-center">
                <ModuleIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-[#FF6B35] font-medium">Module {module.number}</p>
                <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D]">{module.title}</h1>
              </div>
            </div>
            {flowContent?.intention && (
              <p className="text-slate-600 italic">{flowContent.intention}</p>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
            {[
              { id: "intro", label: "Introduction", icon: BookOpen },
              { id: "video", label: "Vidéo", icon: Play },
              { id: "content", label: "Contenu", icon: FileText },
              { id: "exercises", label: "Exercices", icon: Target },
              { id: "anchor", label: "Ancrage", icon: Anchor },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeSection === tab.id
                    ? "bg-[#101E4D] text-white"
                    : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Introduction Section - FLOW Content */}
          {activeSection === "intro" && flowContent && (
            <div className="space-y-6 animate-fade-in" data-testid="intro-section">
              {/* Introduction Text */}
              <Card className="bg-white">
                <CardContent className="p-8">
                  <div className="prose prose-slate max-w-none">
                    <p className="text-slate-700 whitespace-pre-line text-lg leading-relaxed">
                      {flowContent.intro}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Promise Section */}
              <Card className="bg-gradient-to-br from-[#101E4D] to-[#1a2d5c] text-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-[#FF6B35]" />
                    À la fin de ce module, tu {parseInt(moduleId) === 1 ? "sais" : "repars avec"} :
                  </h3>
                  <ul className="space-y-3">
                    {flowContent.promise.map((item, index) => (
                      <li key={`promise-${index}-${item.slice(0,20)}`} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-[#7FD858] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-white/90">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Quote */}
              <div className="text-center py-6">
                <blockquote className="text-xl text-slate-500 italic">
                  « Comprendre précède agir. »
                </blockquote>
              </div>

              {/* Next button */}
              <div className="flex justify-end">
                <Button
                  onClick={() => setActiveSection("video")}
                  className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                >
                  Passer à la vidéo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Video Section */}
          {activeSection === "video" && (
            <div className="space-y-6 animate-fade-in" data-testid="video-section">
              <Card className="bg-white">
                <CardContent className="p-6">
                  {module.video_url ? (
                    <div className="loom-container mb-6">
                      <iframe
                        src={module.video_url.replace('/share/', '/embed/')}
                        title={`Module ${module.number} - ${module.title}`}
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                      <div className="text-center">
                        <Play className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Vidéo à venir</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {progress?.video_watched ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="text-green-700 font-medium">Vidéo vue</span>
                        </>
                      ) : (
                        <span className="text-slate-500">Regardez la vidéo pour continuer</span>
                      )}
                    </div>
                    {!progress?.video_watched && (
                      <Button
                        onClick={handleVideoWatched}
                        className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                        data-testid="mark-video-watched-btn"
                      >
                        Marquer comme vue
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Content Section */}
          {activeSection === "content" && (
            <div className="space-y-6 animate-fade-in" data-testid="content-section">
              {/* Entry by Base */}
              {textBlocks.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-[#101E4D]">
                      <FileText className="w-5 h-5 mr-2 text-[#FF6B35]" />
                      Entrée personnalisée
                      {user?.pcm_base && (
                        <PCMBadge type={user.pcm_base} size="sm" className="ml-3" />
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {textBlocks.map((block, index) => (
                      <div key={block.block_id} className={index > 0 ? "mt-6 pt-6 border-t" : ""}>
                        <h4 className="font-semibold text-[#101E4D] mb-3">{block.title}</h4>
                        <div className="text-slate-600 whitespace-pre-wrap">{block.content}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Questions with Answer Fields */}
              {questionBlocks.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-[#101E4D]">
                      <Lightbulb className="w-5 h-5 mr-2 text-[#FF6B35]" />
                      Questions de réflexion
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {questionBlocks.map((block) => (
                        <div
                          key={block.block_id}
                          className="p-5 bg-slate-50 rounded-xl border-l-4 border-l-[#FF6B35]"
                        >
                          <h4 className="font-medium text-[#101E4D] mb-3">{block.title}</h4>
                          <p className="text-slate-600 mb-4">{block.content}</p>
                          
                          {/* Answer textarea */}
                          <div className="relative">
                            <Textarea
                              placeholder="Écrivez votre réponse ici... (sauvegarde automatique)"
                              value={answers[block.block_id] || ""}
                              onChange={(e) => handleAnswerChange(block.block_id, e.target.value)}
                              className="min-h-[120px] bg-white border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D]"
                              data-testid={`answer-${block.block_id}`}
                            />
                            <div className="absolute bottom-2 right-2 flex items-center">
                              {savingAnswers[block.block_id] === true && (
                                <span className="text-xs text-slate-400 flex items-center">
                                  <div className="w-3 h-3 border-2 border-slate-300 border-t-[#FF6B35] rounded-full animate-spin mr-1"></div>
                                  Enregistrement...
                                </span>
                              )}
                              {savingAnswers[block.block_id] === "saved" && (
                                <span className="text-xs text-green-600 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Enregistré
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Inline comments/conversation */}
                          <AnswerComments 
                            answerId={getAnswerId(block.block_id)}
                            onNewMessage={() => fetchModuleData()}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {textBlocks.length === 0 && questionBlocks.length === 0 && (
                <Card className="bg-white">
                  <CardContent className="py-12 text-center">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Contenu à venir</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Exercises Section */}
          {activeSection === "exercises" && (
            <div className="space-y-6 animate-fade-in" data-testid="exercises-section">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center text-[#101E4D]">
                    <Target className="w-5 h-5 mr-2 text-[#FF6B35]" />
                    Exercices
                    {user?.pcm_phase && (
                      <PCMBadge type={user.pcm_phase} size="sm" className="ml-3" />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {exercises.length > 0 ? (
                    <div className="space-y-6">
                      {exercises.map((exercise) => {
                        const isCompleted = progress?.exercises_completed?.includes(
                          exercise.exercise_id
                        );
                        // Use exercise_id as block_id for answers
                        const exerciseBlockId = `exercise_${exercise.exercise_id}`;
                        const currentAnswer = answers[exerciseBlockId] || "";
                        const isSaving = savingAnswers[exerciseBlockId];

                        return (
                          <div
                            key={exercise.exercise_id}
                            className={`p-5 rounded-xl border transition-all ${
                              isCompleted
                                ? "bg-green-50 border-green-200"
                                : "bg-white border-slate-200"
                            }`}
                            data-testid={`exercise-${exercise.exercise_id}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h4 className="font-semibold text-[#101E4D]">
                                    {exercise.title}
                                  </h4>
                                  {exercise.is_optional && (
                                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                      Optionnel
                                    </span>
                                  )}
                                </div>
                                <p className="text-slate-600 text-sm mb-3">
                                  {exercise.description}
                                </p>
                              </div>
                            </div>

                            <div className="bg-slate-50 rounded-lg p-4 mb-4">
                              <h5 className="text-sm font-medium text-[#101E4D] mb-2">
                                Instructions
                              </h5>
                              <p className="text-sm text-slate-600 whitespace-pre-wrap">
                                {exercise.instructions}
                              </p>
                            </div>

                            {/* Zone de réponse */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-[#101E4D] mb-2">
                                Ta réponse
                              </label>
                              <div className="relative">
                                <Textarea
                                  value={currentAnswer}
                                  onChange={(e) => handleAnswerChange(exerciseBlockId, e.target.value)}
                                  placeholder="Écris ta réponse ici..."
                                  className="min-h-[120px] resize-y border-slate-200 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                                  data-testid={`exercise-answer-${exercise.exercise_id}`}
                                />
                                {isSaving && (
                                  <div className="absolute top-2 right-2 flex items-center text-xs text-slate-400">
                                    <div className="w-3 h-3 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin mr-1" />
                                    Sauvegarde...
                                  </div>
                                )}
                                {!isSaving && currentAnswer && (
                                  <div className="absolute top-2 right-2 flex items-center text-xs text-green-500">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Sauvegardé
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Système de commentaires/échanges */}
                            <AnswerComments 
                              answerId={getAnswerId(exerciseBlockId)}
                              onNewMessage={() => fetchModuleData()}
                            />

                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  checked={isCompleted}
                                  onCheckedChange={() =>
                                    !isCompleted && handleExerciseComplete(exercise.exercise_id)
                                  }
                                  disabled={isCompleted}
                                  id={`exercise-check-${exercise.exercise_id}`}
                                  data-testid={`exercise-checkbox-${exercise.exercise_id}`}
                                />
                                <label
                                  htmlFor={`exercise-check-${exercise.exercise_id}`}
                                  className={`text-sm ${
                                    isCompleted ? "text-green-700" : "text-slate-600"
                                  }`}
                                >
                                  {isCompleted ? "Exercice complété" : "Marquer comme complété"}
                                </label>
                              </div>
                              {isCompleted && (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Exercices à venir</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Anchor Section */}
          {activeSection === "anchor" && (
            <div className="space-y-6 animate-fade-in" data-testid="anchor-section">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center text-[#101E4D]">
                    <Anchor className="w-5 h-5 mr-2 text-[#FF6B35]" />
                    Ancrage & Rituel
                    {user?.pcm_base && (
                      <PCMBadge type={user.pcm_base} size="sm" className="ml-3" />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {anchorBlocks.length > 0 ? (
                    <div className="space-y-6">
                      {anchorBlocks.map((block) => (
                        <div
                          key={block.block_id}
                          className="p-5 bg-gradient-to-r from-[#101E4D]/5 to-transparent rounded-xl border border-[#101E4D]/10"
                        >
                          <h4 className="font-semibold text-[#101E4D] mb-3">{block.title}</h4>
                          <p className="text-slate-600 whitespace-pre-wrap mb-4">{block.content}</p>
                          
                          {/* Answer textarea */}
                          <div className="relative">
                            <Textarea
                              placeholder="Notez vos observations, ressentis ou engagements... (sauvegarde automatique)"
                              value={answers[block.block_id] || ""}
                              onChange={(e) => handleAnswerChange(block.block_id, e.target.value)}
                              className="min-h-[120px] bg-white border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D]"
                              data-testid={`answer-anchor-${block.block_id}`}
                            />
                            <div className="absolute bottom-2 right-2 flex items-center">
                              {savingAnswers[block.block_id] === true && (
                                <span className="text-xs text-slate-400 flex items-center">
                                  <div className="w-3 h-3 border-2 border-slate-300 border-t-[#FF6B35] rounded-full animate-spin mr-1"></div>
                                  Enregistrement...
                                </span>
                              )}
                              {savingAnswers[block.block_id] === "saved" && (
                                <span className="text-xs text-green-600 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Enregistré
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Inline conversation */}
                          <AnswerComments 
                            answerId={getAnswerId(block.block_id)}
                            onNewMessage={() => fetchModuleData()}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <Anchor className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Rituel d'ancrage à venir</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Transition Text or Closing Text */}
              {flowContent && (
                <Card className="bg-slate-50 border-none">
                  <CardContent className="p-6">
                    {flowContent.closing ? (
                      <div className="text-center">
                        <p className="text-slate-700 whitespace-pre-line mb-4">
                          {flowContent.closing}
                        </p>
                        <p className="text-[#101E4D] font-semibold text-lg">
                          « Tu pilotes à nouveau. »
                        </p>
                      </div>
                    ) : flowContent.transition ? (
                      <div className="flex items-start space-x-3">
                        <ArrowRight className="w-5 h-5 text-[#FF6B35] mt-1 flex-shrink-0" />
                        <p className="text-slate-600 italic">{flowContent.transition}</p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              )}

              {/* Validation */}
              <Card
                className={`bg-white ${
                  canValidate ? "border-[#FF6B35] animate-pulse-glow" : ""
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-[#101E4D] mb-1">Valider ce module</h3>
                      <p className="text-sm text-slate-500">
                        {canValidate
                          ? "Tous les exercices obligatoires sont complétés"
                          : "Complétez la vidéo et les exercices obligatoires"}
                      </p>
                    </div>
                    <Button
                      onClick={handleValidateModule}
                      disabled={!canValidate || progress?.validation_completed}
                      className={`${
                        canValidate
                          ? "bg-[#FF6B35] hover:bg-[#E85D2A]"
                          : "bg-slate-300 cursor-not-allowed"
                      } text-white px-8`}
                      data-testid="validate-module-btn"
                    >
                      {progress?.validation_completed ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Module validé
                        </>
                      ) : (
                        <>
                          Valider le module
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ModulePage;
